import { sampleQueryKeys, studyQueryKeys } from '@/config/queryKeys';
import { apiClient } from '@/lib/api-client';
import type { OrgSampleFilters, SampleWithNamesPublic, PaginatedSamplesResponse, SampleSearchRequest } from '@/types';

/**
 * Fetch a single sample by ID within an organization.
 *
 * @param orgId - The organization ID
 * @param sampleId - The sample ID
 * @returns Promise resolving to the sample data
 *
 * @throws {ApiError} If the request fails or the sample is not found
 *
 * @example
 * ```typescript
 * const sample = await getSample('org-123', 'sample-456');
 * console.log(sample.name, sample.study_name);
 * ```
 */
export async function getSample(orgId: string, sampleId: string): Promise<SampleWithNamesPublic> {
  // Backend does not expose a single-sample GET; use the organization-level search endpoint.
  // The OrgSampleFilters type does not include an `id` field, but the backend accepts it at
  // runtime. sort_by/sort_order are required by OrgSampleSearchRequest even though they are
  // semantically irrelevant for an ID equality filter.
  const res = await apiClient.post<PaginatedSamplesResponse>(
    sampleQueryKeys.endpoints.listSearch(orgId),
    {
      skip: 0,
      limit: 1,
      sort_by: 'created_at',
      sort_order: 'desc',
      // The OrgSampleFilters type does not include an `id` field, but the backend accepts it at
      // runtime. Cast to bypass excess property checking.
      filters: {
        id: { conditions: [{ operator: 'equals', value: sampleId }], logic: 'and' },
      } as unknown as OrgSampleFilters,
    },
    { expectJson: true },
  );

  const sample = res?.records?.[0] ?? null;
  if (!sample) throw new Error(`Sample ${sampleId} not found`);
  return sample;
}

export async function searchStudySamples(
  orgId: string,
  studyId: string,
  request: SampleSearchRequest,
): Promise<PaginatedSamplesResponse> {
  const result = await apiClient.post<PaginatedSamplesResponse>(
    studyQueryKeys.endpoints.samplesSearch(orgId, studyId),
    request,
    { expectJson: true },
  );
  if (result === null) throw new Error('searchStudySamples: endpoint returned no content');
  return result;
}
